/**
 * CaricaVotiPage — wizard import voti (FASE 4 iter 6).
 *
 * 4 modalità (tab): XLSX Fantacalcio.it / Paste / CSV / HTML Gazzetta.
 * Submit → POST /admin/giornate/{id}/import-voti(-xlsx) → mostra report.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { ImportVotiPayload, ImportVotiReport, useImportVoti } from "../../hooks/useFase4";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { GiornataSelector } from "../common/GiornataSelector";
import { MobileShell } from "../shell/MobileShell";

type Modalita = "xlsx" | "paste" | "csv" | "html";
type Tipo = "live" | "finale";

export function CaricaVotiPage() {
  const { user } = useAuth();
  const { data: stagioneAttiva } = useStagioneAttiva();
  const stagioneId = stagioneAttiva?.id ?? null;
  const [giornataId, setGiornataId] = useState("");
  const [modalita, setModalita] = useState<Modalita>("xlsx");
  const [tipo, setTipo] = useState<Tipo>("finale");
  const [contenuto, setContenuto] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState<ImportVotiReport | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const importMut = useImportVoti();

  async function submit() {
    if (!giornataId.trim()) return;
    if (modalita === "xlsx") {
      if (!file) return;
      setErrore(null);
      setReport(null);
      setUploading(true);
      try {
        // Converte il file in base64 e lo manda come JSON (no multipart).
        // Più robusto in caso di proxy/CDN che alterano il body multipart.
        const arrayBuf = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuf);
        // btoa accetta solo binary string; converto manualmente in chunk
        // per evitare "Maximum call stack" su file >100KB.
        let binary = "";
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
        }
        const b64 = btoa(binary);
        const payload: ImportVotiPayload = {
          tipo,
          fonte: "manuale_admin",
          xlsx_b64: b64,
        };
        const r = await importMut.mutateAsync({
          giornataId: giornataId.trim(),
          payload,
        });
        setReport(r);
      } catch (e) {
        const err = e as {
          response?: { data?: { detail?: string }; status?: number };
          message?: string;
          code?: string;
        };
        const detail = err.response?.data?.detail;
        const status = err.response?.status;
        const code = err.code;
        let msg: string;
        if (detail) {
          msg = `${detail} (HTTP ${status ?? "?"})`;
        } else if (code === "ECONNABORTED" || err.message?.includes("timeout")) {
          msg =
            "Timeout: l'import ha richiesto > 2 min. " +
            "Verifica i logs Railway, riprova fra qualche secondo.";
        } else if (err.message === "Network Error" || code === "ERR_NETWORK") {
          msg =
            "Network error: il backend non ha risposto (deploy in corso? " +
            "request body 5xx?). Aspetta 30s e riprova.";
        } else {
          msg = err.message ?? "errore sconosciuto";
        }
        setErrore(msg);
      } finally {
        setUploading(false);
      }
      return;
    }
    if (!contenuto.trim()) return;
    setErrore(null);
    setReport(null);
    const fonte =
      modalita === "csv" ? "csv_manuale" : modalita === "paste" ? "paste_manuale" : "scraper";
    const payload: ImportVotiPayload = {
      tipo,
      fonte: fonte as ImportVotiPayload["fonte"],
      csv: modalita === "csv" ? contenuto : null,
      paste: modalita === "paste" ? contenuto : null,
      html_gazzetta: modalita === "html" ? contenuto : null,
    };
    try {
      const r = await importMut.mutateAsync({
        giornataId: giornataId.trim(),
        payload,
      });
      setReport(r);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  const placeholder =
    modalita === "csv"
      ? "Nome,Squadra,Voto,Gol,Assist,Amm,Esp,Auto,GolSubito,PortaInviolata\nDonnarumma,Milan,6.5,0,0,0,0,0,1,SI"
      : modalita === "paste"
        ? "Donnarumma G.    Milan    6,5    0 0 0 0 0 1 SI\nTheo Hernandez    Milan    7    1 0 0 0 0 0 NO"
        : '<table>\n<tr class="riga-voto"><td>Pessina</td><td>Monza</td>...</tr>\n</table>';

  return (
    <MobileShell logoSuffix="CARICA VOTI">
      <h2 className="font-display text-3xl text-accent tracking-wide">Carica voti</h2>
      {/* Andrea 2026-09-29: il foglio voti e' comune a tutte le leghe (si
            propaga alle giornate sorelle): lo carica l'admin dell'app. */}
      {user && !user.is_admin_app ? (
        <div className="card text-sm text-fg-muted">
          I voti li carica l'amministratore dell'app, una volta per tutte le leghe. Tu, da admin di
          lega, calcoli e sistemi le giornate in <b>Calcolo giornata</b>.
        </div>
      ) : (
        <>
          <div className="card text-[11px] text-fg-muted">
            Il foglio caricato qui vale per <b>tutte le leghe</b>: i voti passano da soli alle
            giornate delle altre leghe con la stessa giornata di Serie A. Per controllare o
            correggere i singoli voti:{" "}
            <Link to="/admin/voti-giornata" className="text-accent font-bold">
              Voti caricati →
            </Link>
          </div>

          <div className="card grid grid-cols-1 md:grid-cols-2 gap-3">
            <GiornataSelector stagioneId={stagioneId} value={giornataId} onChange={setGiornataId} />
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                Tipo voti
              </label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as Tipo)}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="finale">Voti finali (definitivi)</option>
                <option value="live">Voti live (parziali, sovrascritti dai finali)</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {(["xlsx", "paste", "csv", "html"] as Modalita[]).map((m) => (
              <button
                key={m}
                onClick={() => setModalita(m)}
                className={`pill ${modalita === m ? "bg-accent text-ink" : "bg-primary-deep/40 text-fg-muted"}`}
              >
                {m === "xlsx"
                  ? "📊 XLSX Fantacalcio.it"
                  : m === "paste"
                    ? "📋 Paste"
                    : m === "csv"
                      ? "📄 CSV"
                      : "🌐 HTML Gazzetta"}
              </button>
            ))}
          </div>

          {modalita === "xlsx" ? (
            <div className="card space-y-3">
              <div>
                <label className="text-xs text-fg-muted uppercase tracking-wider">
                  File XLSX Fantacalcio.it
                </label>
                <div className="text-[10px] text-fg-muted mt-1">
                  Scarica da fantacalcio.it il file{" "}
                  <code className="bg-primary-deep/40 px-1 rounded">
                    Voti_Fantacalcio_Stagione_..._Giornata_N.xlsx
                  </code>{" "}
                  e caricalo qui. Il parser cerca il foglio "Fantacalcio" (voti giornalistici),
                  include tutte le squadre, deduce porta inviolata per i portieri con 0 gol subiti.
                </div>
              </div>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-fg-muted file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-accent file:text-ink file:font-bold hover:file:bg-accent/80"
              />
              {file && (
                <div className="text-xs text-success">
                  ✓ File selezionato: <strong>{file.name}</strong> ({Math.round(file.size / 1024)}{" "}
                  KB)
                </div>
              )}
              <button
                onClick={submit}
                disabled={uploading || !giornataId || !file}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? "Upload in corso…" : "📥 Importa voti dal file XLSX"}
              </button>
            </div>
          ) : (
            <div className="card">
              <label className="text-xs text-fg-muted uppercase tracking-wider">
                {modalita === "csv"
                  ? "Contenuto CSV"
                  : modalita === "paste"
                    ? "Testo paste"
                    : "HTML Gazzetta"}
              </label>
              <textarea
                value={contenuto}
                onChange={(e) => setContenuto(e.target.value)}
                placeholder={placeholder}
                rows={12}
                className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <button
                onClick={submit}
                disabled={importMut.isPending || !giornataId || !contenuto}
                className="btn-primary mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importMut.isPending ? "Importazione…" : "Importa voti"}
              </button>
            </div>
          )}

          {errore && (
            <div className="card bg-danger/10 border-danger/30 text-sm text-danger">{errore}</div>
          )}

          {report && (
            <div className="card space-y-3">
              <h3 className="font-bold text-accent">Report importazione</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs text-fg-muted uppercase">Inseriti</div>
                  <div className="font-display text-2xl text-success">{report.voti_inseriti}</div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">Sotto soglia</div>
                  <div className="font-display text-2xl text-warning">
                    {report.nomi_fuzzy_sotto_soglia.length}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-fg-muted uppercase">Non abbinati</div>
                  <div className="font-display text-2xl text-danger">
                    {report.nomi_non_abbinati.length}
                  </div>
                </div>
              </div>
              {report.nomi_fuzzy_sotto_soglia.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold mt-2">Nomi sotto soglia (con candidati top):</h4>
                  <ul className="text-xs space-y-1 mt-1">
                    {report.nomi_fuzzy_sotto_soglia.map(([nome, squadra, candidati], i) => (
                      <li key={i} className="font-mono bg-warning/10 rounded p-2">
                        <strong>{nome}</strong> ({squadra ?? "—"}) →{" "}
                        {candidati.map(([n, s]) => `${n} ${(s * 100).toFixed(0)}%`).join(", ")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {report.nomi_non_abbinati.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold mt-2">Nomi non abbinati:</h4>
                  <ul className="text-xs font-mono mt-1">
                    {report.nomi_non_abbinati.map(([nome, squadra], i) => (
                      <li key={i}>
                        {nome} ({squadra ?? "—"})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </MobileShell>
  );
}
